import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { founderInvitationApi } from "@/api/deals-api";
import { useWorkspaceStore } from "@/store/workspace-store";
import { CheckCircle, Clock, Loader2, RefreshCw, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import posthog from "posthog-js";

interface FounderInvitationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
  invitationStatus?: {
    hasInvitation: boolean;
    invitation: any;
    companyName: string;
  } | null;
  onInvitationUpdate: () => void;
}

export function FounderInvitationDialog({
  isOpen,
  onClose,
  companyId,
  companyName,
  invitationStatus,
  onInvitationUpdate,
}: FounderInvitationDialogProps) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const [founderEmail, setFounderEmail] = useState("");
  const [founderName, setFounderName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleInviteSubmit = async () => {
    if (!founderEmail || !founderName) return;

    setIsLoading(true);
    try {
      const response = await founderInvitationApi.inviteFounderToCompany(
        companyId,
        founderEmail,
        founderName
      );

      if (response.status === "success") {
        // Track founder invite sent event
        posthog.capture("founder_invite_sent", {
          workspace_id: activeWorkspaceId,
          deal_id: companyId,
          founder_email: founderEmail,
        });

        toast.success("Founder invitation sent successfully!");
        setFounderEmail("");
        setFounderName("");
        onInvitationUpdate();
        onClose();
      } else {
        toast.error(response.message || "Failed to send invitation");
      }
    } catch (error) {
      toast.error("Failed to send invitation");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendInvitation = async () => {
    setIsLoading(true);
    try {
      await founderInvitationApi.resendFounderInvitation(companyId);
      toast.success("Invitation resent successfully!");
      onInvitationUpdate();
    } catch (error) {
      toast.error("Failed to resend invitation");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelInvitation = async () => {
    try {
      await founderInvitationApi.cancelFounderInvitation(companyId);
      toast.success("Invitation cancelled successfully!");
      onInvitationUpdate();
      onClose();
    } catch (error) {
      toast.error("Failed to cancel invitation");
    }
  };

  const getInvitationStatusBadge = () => {
    if (!invitationStatus?.hasInvitation) return null;

    const { invitation } = invitationStatus;
    const isExpired = new Date(invitation.expires_at) < new Date();

    if (isExpired) {
      return (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <Clock className="h-4 w-4 text-red-500" />
          <div>
            <p className="text-sm font-medium text-red-700">
              Invitation Expired
            </p>
            <p className="text-xs text-red-600">
              This invitation has expired and is no longer valid.
            </p>
          </div>
        </div>
      );
    }

    switch (invitation.invitation_status) {
      case "pending":
        return (
          <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Clock className="h-4 w-4 text-yellow-500" />
            <div>
              <p className="text-sm font-medium text-yellow-700">
                Invitation Pending
              </p>
              <p className="text-xs text-yellow-600">
                Waiting for the founder to accept the invitation.
              </p>
            </div>
          </div>
        );
      case "accepted":
        return (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <div>
              <p className="text-sm font-medium text-green-700">
                Invitation Accepted
              </p>
              <p className="text-xs text-green-600">
                The founder has accepted the invitation.
              </p>
            </div>
          </div>
        );
      case "declined":
        return (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <XCircle className="h-4 w-4 text-red-500" />
            <div>
              <p className="text-sm font-medium text-red-700">
                Invitation Declined
              </p>
              <p className="text-xs text-red-600">
                The founder has declined the invitation.
              </p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const handleClose = () => {
    setFounderEmail("");
    setFounderName("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {invitationStatus?.hasInvitation
              ? "Manage Founder Invitation"
              : "Invite Founder"}
          </DialogTitle>
          <DialogDescription>
            {invitationStatus?.hasInvitation
              ? `Manage the founder invitation for ${companyName}`
              : `Send an invitation to a founder to manage ${companyName}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Show invite form if no invitation exists */}
          {!invitationStatus?.hasInvitation && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="founderName">Founder Name</Label>
                <Input
                  id="founderName"
                  placeholder="Enter founder's full name"
                  value={founderName}
                  onChange={(e) => setFounderName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="founderEmail">Founder Email</Label>
                <Input
                  id="founderEmail"
                  type="email"
                  placeholder="Enter founder's email address"
                  value={founderEmail}
                  onChange={(e) => setFounderEmail(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Show invitation management if invitation exists */}
          {invitationStatus?.hasInvitation && (
            <>
              {/* Invitation Status */}
              {getInvitationStatusBadge()}

              {/* Invitation Details */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Invited:</span>
                    <span className="font-medium">
                      {invitationStatus.invitation.invited_name}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">
                      {invitationStatus.invitation.invited_email}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Expires:</span>
                    <span className="font-medium">
                      {new Date(
                        invitationStatus.invitation.expires_at
                      ).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {/* Show invite form actions if no invitation */}
          {!invitationStatus?.hasInvitation && (
            <>
              <Button
                onClick={handleInviteSubmit}
                disabled={!founderEmail || !founderName || isLoading}
                className="w-full sm:w-auto"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : null}
                Send Invitation
              </Button>
              <Button
                onClick={handleClose}
                variant="outline"
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
            </>
          )}

          {/* Show management actions if invitation exists */}
          {invitationStatus?.hasInvitation && (
            <>
              <Button
                onClick={handleResendInvitation}
                disabled={isLoading}
                className="w-full sm:w-auto"
                variant="outline"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Resend Invitation
              </Button>
              <Button
                onClick={handleCancelInvitation}
                variant="destructive"
                className="w-full sm:w-auto"
              >
                Cancel Invitation
              </Button>
              <Button
                onClick={handleClose}
                variant="outline"
                className="w-full sm:w-auto"
              >
                Close
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
